import React from 'react';
import { computeKpiProgress, kpisConfigured, formatKpiValue } from '../utils/campaignKpis';

// English ordinal date, e.g. "31st of August 2026".
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
const ordinalDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return `${ordinal(dt.getDate())} of ${dt.toLocaleDateString('en-GB', { month: 'long' })} ${dt.getFullYear()}`;
};

// Over/under-achievement badge vs the KPI target (uncapped).
const OverUnderBadge = ({ rawPct }) => {
  if (rawPct == null) return null;
  const delta = Math.round(rawPct - 100);
  if (delta === 0) return <span className="text-[11px] font-semibold text-sc-pink">On target</span>;
  const over = delta > 0;
  return (
    <span className={`text-[11px] font-semibold ${over ? 'text-sc-pink' : 'text-white'}`}>
      {over ? `+${delta}%` : `${delta}%`}
      <span className="font-normal text-white"> {over ? 'over' : 'under'}</span>
    </span>
  );
};

// One compact KPI pill: label + over/under on top, value/target, thin progress bar.
const KpiPill = ({ row }) => (
  <div className="rounded-lg border border-white bg-gradient-to-r from-pink-500/20 to-purple-500/20 px-3 py-1.5 min-w-[160px] flex-1 sm:flex-none">
    <div className="flex items-center justify-between gap-3 mb-0.5">
      <span className="text-[11px] text-white">{row.label}</span>
      <OverUnderBadge rawPct={row.rawPct} />
    </div>
    <div className="text-sm font-semibold text-white leading-tight">
      {formatKpiValue(row.actual, row.metric)}
      <span className="text-white font-normal text-xs"> / {formatKpiValue(row.target, row.metric)}</span>
    </div>
    <div className="mt-1.5 h-1 rounded-full bg-white/10 overflow-hidden">
      <div
        className="h-1 rounded-full bg-sc-pink transition-all duration-500"
        style={{ width: `${Math.max(0, Math.min(100, row.pct))}%` }}
      />
    </div>
  </div>
);

// Internal-only KPI progress panel on the campaign page.
const CampaignKpiProgress = ({ kpis, contents, keysAvailable }) => {
  if (!kpisConfigured(kpis)) return null;
  const prog = computeKpiProgress(kpis, contents || []);
  const keys = Number(keysAvailable) || 0;
  const coverage = (contents || []).length; // pieces of coverage from the creators
  const coveragePct = keys > 0 ? Math.round((coverage / keys) * 100) : 0;

  const win = prog.organic.window;
  let windowLabel = '';
  if (prog.organic.rows.length > 0 && win) {
    if (win.mode === 'monthly') {
      windowLabel = `Month ${win.monthNumber}: ${ordinalDate(win.start)} to ${ordinalDate(win.end)}`;
    } else {
      windowLabel = win.end ? `Through to the ${ordinalDate(win.end)}` : 'Whole project';
    }
  }

  return (
    <div className="mb-6 w-fit max-w-full rounded-xl border border-white bg-[#241d33] px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2.5 text-sm">
        <h3 className="font-semibold text-white">Campaign KPIs</h3>
        {keys > 0 && (
          <>
            <span className="text-white">|</span>
            <span className="text-white"><span className="font-semibold text-sc-pink">{keys.toLocaleString()}</span> Keys</span>
            <span className="text-white">|</span>
            <span className="text-white"><span className="font-semibold text-sc-pink">{coverage.toLocaleString()}</span> Pieces of Content</span>
            <span className="text-white">|</span>
            <span className="text-white"><span className="font-semibold text-sc-pink">{coveragePct}%</span> of Coverage vs Keys Distributed</span>
          </>
        )}
      </div>

      {/* Sponsored and Organic groups sit side by side, wrapping if the row runs out of space. */}
      <div className="flex flex-wrap gap-x-10 gap-y-3">
        {prog.sponsored.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-sc-pink mb-1.5">Sponsored</div>
            <div className="flex flex-wrap gap-2">
              {prog.sponsored.map((row, i) => <KpiPill key={i} row={row} />)}
            </div>
          </div>
        )}

        {prog.organic.rows.length > 0 && (
          <div>
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="text-xs font-semibold text-sc-pink">Organic</span>
              {windowLabel && <span className="text-[11px] text-white">{windowLabel}</span>}
            </div>
            <div className="flex flex-wrap gap-2">
              {prog.organic.rows.map((row, i) => <KpiPill key={i} row={row} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignKpiProgress;
